import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  getPeriodsByGroup,
  getActivePeriod,
  createPeriod as createPeriodService,
  closePeriod as closePeriodService,
} from '../services/periodService';
import {
  COLLECTIONS,
  GROUPS_FIELDS,
  GROUP_MEMBERS_FIELDS,
  GROUP_MEMBER_STATUS,
  PERIODS_FIELDS,
  HALL_OF_FAME_FIELDS,
  USERS_FIELDS,
  RATING_MODES,
  RATING_PRESETS,
  SEASON_START_OPTIONS,
} from '../domain/dataModel';

function formatDate(it) {
  if (!it?.toDate) return '—';
  return it.toDate().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
}

function IconArrowLeft({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function Periods() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [group, setGroup] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [members, setMembers] = useState([]);
  const [hallOfFameDocs, setHallOfFameDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [closeLoading, setCloseLoading] = useState(false);
  const [closeSuccess, setCloseSuccess] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [ratingMode, setRatingMode] = useState(RATING_MODES.RESULT_ONLY);
  const [ratingPreset, setRatingPreset] = useState(RATING_PRESETS.CLASSIC);
  const [seasonStartOption, setSeasonStartOption] = useState(SEASON_START_OPTIONS.RESET);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedPeriodRanking, setSelectedPeriodRanking] = useState(null);
  const [usersMap, setUsersMap] = useState({});

  const today = useMemo(() => dayjs().startOf('day').toDate(), []);
  const [activePeriod, setActivePeriod] = useState(null);

  const closedPeriods = useMemo(() => {
    const now = new Date();
    return periods
      .filter((p) => {
        const end = p[PERIODS_FIELDS.END_DATE]?.toDate?.();
        return end && end < now;
      })
      .sort((a, b) => {
        const ea = a[PERIODS_FIELDS.END_DATE]?.toMillis?.() ?? 0;
        const eb = b[PERIODS_FIELDS.END_DATE]?.toMillis?.() ?? 0;
        return eb - ea;
      });
  }, [periods]);

  // Load group and check owner
  useEffect(() => {
    if (!groupId || !user?.uid) return;
    let cancelled = false;
    getDoc(doc(db, COLLECTIONS.GROUPS, groupId))
      .then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          setError('Gruppo non trovato');
          setLoading(false);
          return;
        }
        const data = { id: snap.id, ...snap.data() };
        if (data[GROUPS_FIELDS.OWNER_ID] !== user.uid) {
          navigate(`/groups/${groupId}`, { replace: true });
          return;
        }
        setGroup(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Errore di caricamento');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [groupId, user?.uid, navigate]);

  // Load periods
  useEffect(() => {
    if (!groupId) return;
    getPeriodsByGroup(groupId).then(setPeriods).catch(() => setPeriods([]));
  }, [groupId]);

  // Load active period
  useEffect(() => {
    if (!groupId) return;
    getActivePeriod(groupId).then(setActivePeriod).catch(() => setActivePeriod(null));
  }, [groupId, periods]);

  // Load active members
  useEffect(() => {
    if (!groupId) return;
    const q = query(
      collection(db, COLLECTIONS.GROUP_MEMBERS),
      where(GROUP_MEMBERS_FIELDS.GROUP_ID, '==', groupId),
      where(GROUP_MEMBERS_FIELDS.STATUS, '==', GROUP_MEMBER_STATUS.ACTIVE)
    );
    getDocs(q).then((snap) => {
      setMembers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }).catch(() => setMembers([]));
  }, [groupId]);

  // Load hallOfFame docs for this group (for rankings by period)
  useEffect(() => {
    if (!groupId) return;
    const q = query(
      collection(db, COLLECTIONS.HALL_OF_FAME),
      where(HALL_OF_FAME_FIELDS.GROUP_ID, '==', groupId)
    );
    getDocs(q).then((snap) => {
      setHallOfFameDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }).catch(() => setHallOfFameDocs([]));
  }, [groupId]);

  // Load user names for ranking display
  const rankingUserIds = useMemo(() => {
    const ids = new Set();
    hallOfFameDocs.forEach((doc) => {
      const arr = doc[HALL_OF_FAME_FIELDS.RANKINGS] || [];
      arr.forEach((r) => r.memberId && ids.add(r.memberId));
    });
    return [...ids];
  }, [hallOfFameDocs]);

  useEffect(() => {
    if (rankingUserIds.length === 0) {
      setUsersMap({});
      return;
    }
    const load = async () => {
      const map = {};
      await Promise.all(
        rankingUserIds.map(async (uid) => {
          const snap = await getDoc(doc(db, COLLECTIONS.USERS, uid));
          if (snap.exists()) {
            const d = snap.data();
            map[uid] = d[USERS_FIELDS.DISPLAY_NAME] || d[USERS_FIELDS.EMAIL] || uid;
          } else {
            map[uid] = uid;
          }
        })
      );
      setUsersMap(map);
    };
    load();
  }, [rankingUserIds]);

  const handleClosePeriod = async () => {
    if (!activePeriod || !groupId) return;
    setCloseLoading(true);
    setCloseSuccess(false);
    setError(null);
    try {
      await closePeriodService(activePeriod.id, groupId);
      const updatedPeriods = await getPeriodsByGroup(groupId);
      setPeriods(updatedPeriods);
      setActivePeriod(await getActivePeriod(groupId));
      setCloseSuccess(true);
    } catch (err) {
      setError(err.message || 'Errore nella chiusura del periodo');
    } finally {
      setCloseLoading(false);
    }
  };

  const handleCreatePeriod = async (e) => {
    e.preventDefault();
    setCreateError('');
    const start = startDate ? (startDate.toDate ? startDate.toDate() : startDate) : null;
    const end = endDate ? (endDate.toDate ? endDate.toDate() : endDate) : null;
    if (!description.trim()) {
      setCreateError('Inserisci una descrizione.');
      return;
    }
    if (!start || !end) {
      setCreateError('Inserisci data inizio e data fine.');
      return;
    }
    if (start > today) {
      setCreateError('La data inizio non può essere nel futuro.');
      return;
    }
    if (end <= start) {
      setCreateError('La data fine deve essere successiva alla data inizio.');
      return;
    }
    const now = new Date();
    const newStartDate = start;
    const newEndDate = end;
    const overlapping = periods.filter((p) => {
      const endRaw = p[PERIODS_FIELDS.END_DATE];
      if (!endRaw) return false;
      const endDate = endRaw.toDate ? endRaw.toDate() : new Date(endRaw);
      if (endDate < now) return false;
      const startRaw = p[PERIODS_FIELDS.START_DATE];
      if (!startRaw) return false;
      const startDate = startRaw.toDate ? startRaw.toDate() : new Date(startRaw);
      return startDate <= newEndDate && endDate >= newStartDate;
    });
    if (overlapping.length > 0) {
      setCreateError('Esiste già un periodo che si sovrappone a questo intervallo.');
      return;
    }
    setCreateLoading(true);
    try {
      await createPeriodService(
        groupId,
        {
          name: description.trim(),
          startDate: start,
          endDate: end,
          ratingMode,
          ratingPreset,
          seasonStartOption,
          previousPeriodId:
            seasonStartOption === SEASON_START_OPTIONS.CARRY_OVER
              ? closedPeriods[0]?.id ?? null
              : null,
        },
        user.uid,
        members
      );
      const updatedPeriods = await getPeriodsByGroup(groupId);
      setPeriods(updatedPeriods);
      setActivePeriod(await getActivePeriod(groupId));
      setDescription('');
      setStartDate(null);
      setEndDate(null);
      setRatingMode(RATING_MODES.RESULT_ONLY);
      setRatingPreset(RATING_PRESETS.CLASSIC);
      setSeasonStartOption(SEASON_START_OPTIONS.RESET);
    } catch (err) {
      setCreateError(err.message || 'Errore nella creazione del periodo');
    } finally {
      setCreateLoading(false);
    }
  };

  const openRankingDrawer = (period) => {
    const hof = hallOfFameDocs.find((d) => d[HALL_OF_FAME_FIELDS.PERIOD_ID] === period.id);
    setSelectedPeriodRanking({ period, rankings: hof?.[HALL_OF_FAME_FIELDS.RANKINGS] || [] });
    setDrawerOpen(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-royal border-t-transparent" />
      </div>
    );
  }

  if (error && !group) {
    return (
      <div className="space-y-4">
        <Button type="button" variant="outline" onClick={() => navigate(`/groups/${groupId}`)} className="w-fit">
          <IconArrowLeft className="mr-2 h-4 w-4" />
          Indietro
        </Button>
        <div className="rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
          {error}
        </div>
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
      <div className="space-y-5">
        {/* 1. INTESTAZIONE */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/groups/${groupId}`)}
            className="w-fit"
          >
            <IconArrowLeft className="mr-2 h-4 w-4" />
            Indietro
          </Button>
          <h1 className="font-display text-xl font-bold text-brand-ice sm:text-2xl">
            Gestione Periodi — {group?.[GROUPS_FIELDS.NAME] || 'Gruppo'}
          </h1>
        </div>

        {error && (
          <div className="rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
            {error}
          </div>
        )}

        {/* 2. PERIODO ATTIVO */}
        <div className="space-y-2">
          <h2 className="font-display text-lg font-semibold text-brand-ice">Periodo attivo</h2>
          {activePeriod ? (
            <Card className="border-brand-royal/30 bg-brand-royal/10">
              <CardContent className="space-y-3 p-4">
                <p className="font-display text-base font-bold text-brand-ice">
                  {activePeriod[PERIODS_FIELDS.NAME]}
                </p>
                <p className="text-sm text-brand-ice/70">
                  Da {formatDate(activePeriod[PERIODS_FIELDS.START_DATE])} a{' '}
                  {formatDate(activePeriod[PERIODS_FIELDS.END_DATE])}
                </p>
                {closeSuccess && (
                  <div className="rounded-btn border border-win/60 bg-win/15 px-4 py-3 text-sm text-win">
                    Periodo chiuso con successo.
                  </div>
                )}
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setConfirmCloseOpen(true)}
                  disabled={closeLoading}
                  className="w-full sm:w-fit"
                >
                  {closeLoading ? 'Chiusura in corso...' : 'Chiudi periodo'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-brand-ice/60">Nessun periodo attivo.</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* 3. CREA NUOVO PERIODO */}
        <div className="space-y-2">
          <h2 className="font-display text-lg font-semibold text-brand-ice">Crea nuovo periodo</h2>
          {activePeriod ? (
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-brand-ice/60">
                  Chiudi il periodo attivo prima di crearne uno nuovo
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="relative p-4">
                {createError && (
                  <div className="relative z-0 mb-4 rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
                    {createError}
                  </div>
                )}
                <form onSubmit={handleCreatePeriod} className="relative z-10 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="period-description">Descrizione</Label>
                    <Input
                      id="period-description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Es. Anno 2026"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <DatePicker
                        label="Data inizio"
                        value={startDate}
                        onChange={setStartDate}
                        maxDate={dayjs()}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </div>
                    <div className="space-y-2">
                      <DatePicker
                        label="Data fine"
                        value={endDate}
                        onChange={setEndDate}
                        minDate={startDate || undefined}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Modalità ranking</Label>
                    <p className="text-xs text-brand-ice/50">
                      Definisce come vengono calcolati i punti ELO. Non modificabile dopo la prima partita ufficiale.
                    </p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {[
                        {
                          value: RATING_MODES.RESULT_ONLY,
                          label: 'Standard',
                          desc: 'Conta solo il risultato. Semplice e diretto.',
                        },
                        {
                          value: RATING_MODES.EXPECTED_PERFORMANCE,
                          label: 'Bilanciato',
                          desc: 'Conta anche il divario di forza. Più meritocratico.',
                        },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setRatingMode(opt.value)}
                          className={`rounded-card border p-3 text-left transition-colors ${
                            ratingMode === opt.value
                              ? 'border-brand-royal bg-brand-royal/15 text-brand-ice'
                              : 'border-surface-muted bg-surface text-brand-ice/60 hover:border-brand-royal/40'
                          }`}
                        >
                          <p className="font-semibold text-sm">{opt.label}</p>
                          <p className="text-xs mt-0.5 opacity-70">{opt.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Peso dei formati</Label>
                    <p className="text-xs text-brand-ice/50">
                      Quanto influiscono King e Americano sul ranking rispetto a una partita classica.
                    </p>
                    <div className="grid grid-cols-1 gap-2">
                      {[
                        {
                          value: RATING_PRESETS.CLASSIC,
                          label: 'Solo le classiche contano',
                          examples: 'Classica +13 pt · King 0 pt · Americano +7 pt',
                        },
                        {
                          value: RATING_PRESETS.BALANCED,
                          label: 'Tutto conta, le classiche di più',
                          examples: 'Classica +13 pt · King +6 pt · Americano +10 pt',
                        },
                        {
                          value: RATING_PRESETS.TOTAL,
                          label: 'Tutto conta uguale',
                          examples: 'Classica +13 pt · King +15 pt · Americano +15 pt',
                        },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setRatingPreset(opt.value)}
                          className={`rounded-card border p-3 text-left transition-colors ${
                            ratingPreset === opt.value
                              ? 'border-brand-royal bg-brand-royal/15 text-brand-ice'
                              : 'border-surface-muted bg-surface text-brand-ice/60 hover:border-brand-royal/40'
                          }`}
                        >
                          <p className="font-semibold text-sm">{opt.label}</p>
                          <p className="text-xs mt-1 font-mono opacity-60">{opt.examples}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Rating iniziale giocatori</Label>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {[
                        {
                          value: SEASON_START_OPTIONS.RESET,
                          label: 'Riparto da 1000',
                          desc: 'Tutti i giocatori iniziano da zero.',
                        },
                        {
                          value: SEASON_START_OPTIONS.CARRY_OVER,
                          label: 'Continua dalla stagione precedente',
                          desc: 'Il rating finale della stagione precedente diventa il punto di partenza.',
                          disabled: closedPeriods.length === 0,
                        },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={opt.disabled}
                          onClick={() => !opt.disabled && setSeasonStartOption(opt.value)}
                          className={`rounded-card border p-3 text-left transition-colors ${
                            opt.disabled
                              ? 'cursor-not-allowed border-surface-muted bg-surface opacity-40'
                              : seasonStartOption === opt.value
                                ? 'border-brand-royal bg-brand-royal/15 text-brand-ice'
                                : 'border-surface-muted bg-surface text-brand-ice/60 hover:border-brand-royal/40'
                          }`}
                        >
                          <p className="font-semibold text-sm">{opt.label}</p>
                          <p className="text-xs mt-0.5 opacity-70">{opt.desc}</p>
                          {opt.disabled && (
                            <p className="text-xs mt-1 text-brand-ice/40">Nessuna stagione precedente disponibile</p>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button type="submit" disabled={createLoading} className="w-full sm:w-fit">
                    {createLoading ? 'Creazione...' : 'Crea periodo'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>

        {/* 4. PERIODI PRECEDENTI */}
        <div className="space-y-2">
          <h2 className="font-display text-lg font-semibold text-brand-ice">Periodi precedenti</h2>
          {closedPeriods.length === 0 ? (
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-brand-ice/60">Nessun periodo chiuso.</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="divide-y divide-surface-muted">
                  {closedPeriods.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => openRankingDrawer(p)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-muted/30"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium text-brand-ice">
                          {p[PERIODS_FIELDS.NAME]}
                        </p>
                        <p className="truncate text-sm text-brand-ice/70">
                          Da {formatDate(p[PERIODS_FIELDS.START_DATE])} a {formatDate(p[PERIODS_FIELDS.END_DATE])}
                        </p>
                      </div>
                      <span className="shrink-0 rounded-badge border border-surface-muted/70 bg-surface-muted/20 px-2.5 py-0.5 text-xs font-semibold text-brand-ice/60">
                        Chiuso
                      </span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Pannello classifica periodo */}
        {drawerOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/60"
            onClick={() => setDrawerOpen(false)}
            role="presentation"
          >
            <div
              className="fixed right-0 top-0 z-50 flex h-full w-full flex-col bg-surface-card sm:w-96"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <h2 className="font-display text-lg font-semibold text-brand-ice">
                  {selectedPeriodRanking?.period?.[PERIODS_FIELDS.NAME] || 'Periodo'} - Classifica finale
                </h2>

                {selectedPeriodRanking?.rankings?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedPeriodRanking.rankings.map((r) => (
                      <div
                        key={r.position}
                        className="grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-3 rounded-card border border-surface-muted bg-surface px-3 py-2"
                      >
                        <span className="text-sm font-semibold text-brand-ice/80">
                          {r.position}
                        </span>
                        <span className="truncate text-sm font-medium text-brand-ice">
                          {usersMap[r.memberId] ?? r.memberId}
                        </span>
                        <span className="text-right text-sm font-semibold text-gold tabular-nums">
                          {r.finalRating ?? '—'}
                        </span>
                        <span className="text-right text-sm text-brand-ice/70 tabular-nums">
                          {r.matchesPlayed ?? 0}
                        </span>
                        <span className="text-right text-sm text-brand-ice/70 tabular-nums">
                          {(r.winRate ?? 0)}%
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-brand-ice/60">Nessun dato di classifica.</p>
                )}
              </div>

              <div className="border-t border-surface-muted p-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setDrawerOpen(false)}
                >
                  Chiudi
                </Button>
              </div>
            </div>
          </div>
        )}

        {confirmCloseOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
            onClick={() => setConfirmCloseOpen(false)}
            role="presentation"
          >
            <div
              className="w-full max-w-md rounded-card border border-surface-muted bg-surface-card p-6 space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-display text-xl font-semibold text-brand-ice">Chiudi periodo?</h3>
              <p className="text-sm text-brand-ice/80 leading-relaxed">
                Stai per chiudere il periodo {activePeriod?.[PERIODS_FIELDS.NAME] || 'selezionato'}. La classifica finale verrà salvata nell'Albo d'oro del gruppo. Potrai riaprire questo periodo solo se non crei un nuovo periodo attivo. Sei sicuro di voler procedere?
              </p>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setConfirmCloseOpen(false)}>
                  Annulla
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setConfirmCloseOpen(false);
                    handleClosePeriod();
                  }}
                  disabled={closeLoading}
                >
                  {closeLoading ? 'Chiusura in corso...' : 'Chiudi periodo'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
}
